import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import Modal from './Modal';

const PdfInstructionsModal = ({
  isOpen,
  onClose,
  configId,
  sectionsInstructions,
  onSave,
  onBack,
}) => {
  const [sections, setSections] = useState(
    Object.keys(sectionsInstructions) || [],
  );

  const [sectionInstructions, setSectionInstructions] = useState(
    sectionsInstructions && typeof sectionsInstructions === 'object'
      ? sectionsInstructions
      : {},
  );

  const [reviewedSections, setReviewedSections] = useState(
    Array.isArray(sections)
      ? sections.reduce((acc, section) => ({ ...acc, [section]: false }), {})
      : {},
  );
  const [selectedSection, setSelectedSection] = useState(sections[0] || null);
  const isSmallScreen = window.innerWidth <= 768;
  const allSectionsReviewed = Object.values(reviewedSections).every(
    (isReviewed) => isReviewed,
  );

  // Refs for scroll positions
  const pdfContentRef = useRef(null);

  useEffect(() => {
    if (sections.length > 0 && !selectedSection) {
      setSelectedSection(sections[0]);
    }
  }, [sections, selectedSection]);

  useEffect(() => {
    // Only scroll to bottom when all sections are reviewed and no change in review status
    if (allSectionsReviewed && pdfContentRef.current) {
      pdfContentRef.current.scrollTo({
        top: pdfContentRef.current.scrollHeight, // Scroll to the bottom
        behavior: 'smooth', // Smooth scroll effect
      });
    }
  }, [allSectionsReviewed]); // This hook will run whenever `allSectionsReviewed` changes

  const handleSectionInstructionChange = (section, newInstruction) => {
    setSectionInstructions((prevSectionInstructions) => {
      // Update the instruction for the specified section
      return {
        ...prevSectionInstructions,
        [section]: newInstruction,
      };
    });
  };

  const isSectionFilled = (section) => {
    // Check if text area in the selected section is filled
    return (
      sectionInstructions[section] && sectionInstructions[section].trim() !== ''
    );
  };

  const handleMarkAsReviewed = (section) => {
    // Mark section as reviewed only if all text areas are filled
    if (isSectionFilled(section)) {
      markSectionAsReviewed(section); // Proceed to mark as reviewed
    } else {
      // Optionally show an alert or message that all fields must be filled
      alert(
        'Please fill all text areas before marking this section as reviewed.',
      );
    }
  };

  const markSectionAsReviewed = (section) => {
    setReviewedSections((prevReviewed) => ({
      ...prevReviewed,
      [section]: !prevReviewed[section],
    }));
  };

  const handleSave = async () => {
    // Save trimmed data and close the modal or dialog
    await onSave(configId, sectionInstructions);
    onClose();
  };

  const buttonStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    marginBottom: '0.5rem',
    padding: '0.75rem 1.25rem',
    border: 'none',
    boxShadow: 'none',
    borderColor: 'transparent',
    borderRadius: '8px',
    cursor: 'pointer',
  };

  const renderSectionButtons = () => {
    return sections.map((section) => {
      const isActive = selectedSection === section;
      const activeStyle = {
        backgroundColor: isActive ? '#6bd888' : '#f9f9f9',
        color: isActive ? 'white' : 'black',
      };

      return (
        <Button
          key={section}
          onClick={() => {
            setSelectedSection(section);

            // Reset scroll positions when section changes
            if (pdfContentRef.current) {
              pdfContentRef.current.scrollTo(0, 0);
            }
          }}
          style={{
            ...buttonStyle,
            ...activeStyle,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <input
            type="checkbox"
            checked={reviewedSections[section] || false}
            onChange={() => handleMarkAsReviewed(section)}
          />
          <span style={{ marginLeft: '8px' }}>{section}</span>{' '}
          {/* Ensure section name is displayed */}
        </Button>
      );
    });
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      fullScreen={true}
      isCloseButton={false}
    >
      <div style={{ textAlign: 'center', margin: '30px 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '14px',
              width: '80%',
              height: '2px',
              backgroundColor: '#ccc',
              zIndex: 0,
            }}
          />
          {[
            'Section Extraction',
            'Content Extraction',
            'Instructions Generation',
          ].map((step, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                zIndex: 1,
                animation: `fadeInSlide 0.5s ${idx * 0.15}s ease forwards`,
                opacity: 0,
                margin: '0 10px', // Spacing between steps,
                cursor: idx < 2 ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (idx === 0) {
                  onBack(0); // Call onBack for the first step
                }
                if (idx === 1) {
                  onBack(1); // Call onBack for the second step
                }
              }}
            >
              <div
                style={{
                  backgroundColor: idx > 2 ? '#6c757d' : '#6bd888', // Different colors for each step
                  color: '#fff',
                  width: '32px', // Slightly larger circle
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '500',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', // Subtle shadow for better contrast
                  transition: 'transform 0.2s ease, background-color 0.3s ease',
                  transform: idx === 2 ? 'scale(1.4)' : 'scale(1)', // Slight scaling for active step
                }}
              >
                {idx + 1}
              </div>
              <div
                style={{
                  fontSize: '13px', // Slightly larger font for better readability
                  marginTop: '5px',
                  color: '#6bd888', // Active step text color
                }}
              >
                {step}
              </div>
            </div>
          ))}
        </div>
        <div style={{ boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
          <p
            style={{
              fontWeight: '900',
              fontSize: '15px',
              color: '#666',
              margin: '0 auto',
              backgroundColor: '#FFF3CD',
            }}
          >
            <i
              className="fa fa-exclamation-circle"
              aria-hidden="true"
              style={{ color: '#f76c6c', marginRight: '4px' }}
            />
            Review each instruction carefully—they’ll shape your future notes.
            Better instructions mean better notes. Update before finalizing.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            height: '70vh',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            margin: '0 auto',
            flexWrap: 'wrap',
            flexDirection: isSmallScreen ? 'column' : 'row',
          }}
        >
          {/* Section Buttons */}
          <div
            className="scrollable-container"
            style={{
              display: 'flex',
              flex: isSmallScreen ? '0 1 auto' : '0.5',
              overflowX: 'scroll',
              overflowY: 'scroll',
              maxHeight: '100%',
              maxWidth: isSmallScreen ? '100%' : '200px',
              padding: '10px',
              borderBottom: isSmallScreen ? '1px solid #ccc' : 'unset',
              borderRight: !isSmallScreen ? '1px solid #ccc' : 'unset',
              flexDirection: isSmallScreen ? 'row' : 'column',
              whiteSpace: isSmallScreen ? 'nowrap' : 'unset',
            }}
          >
            {renderSectionButtons()}
          </div>

          <style>
            {`
              .scrollable-container::-webkit-scrollbar {
                width: 5px; /* Default width of vertical scrollbar */
                height: 5px; /* Adjusted height for horizontal scrollbar */
              }
              .scrollable-container::-webkit-scrollbar-thumb {
                background-color: #888;
                border-radius: 6px;
              }
              .scrollable-container::-webkit-scrollbar-thumb:hover {
                background-color: #555;
              }
              .scrollable-container::-webkit-scrollbar-track {
                background-color: #f0f0f0;
                border-radius: 6px;
              }
          `}
          </style>
          {/* Section Content */}
          <div
            className="scrollable-container"
            style={{
              flex: isSmallScreen ? '1' : '2',
              maxHeight: '500px',
              overflowY: 'scroll', // Always show vertical scrollbar
              padding: '10px',
            }}
            ref={pdfContentRef} // Add ref for PDF content scroll
          >
            {/* Review Button */}
            <Button
              color={reviewedSections[selectedSection] ? 'primary' : 'danger'}
              onClick={() => handleMarkAsReviewed(selectedSection)}
              style={{
                marginBottom: '10px',
                fontSize: isSmallScreen ? '0.9rem' : '0.8rem',
              }}
              size={isSmallScreen ? 'sm' : 'md'}
            >
              {reviewedSections[selectedSection]
                ? 'Reviewed'
                : 'Mark as Reviewed'}
            </Button>

            {/* Text Areas */}
            <div style={{ fontSize: '16px', color: '#555', lineHeight: '1.5' }}>
              {sectionInstructions[selectedSection] !== undefined && (
                <div
                  style={{
                    marginBottom: '10px',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <label
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '5px',
                      color: '#333',
                    }}
                  >
                    Final Instruction for {selectedSection}
                  </label>

                  <textarea
                    value={sectionInstructions[selectedSection] || ''}
                    placeholder={
                      sectionInstructions[selectedSection]
                        ? ''
                        : 'Instruction not generated. Please add it according to you.'
                    }
                    onChange={(e) =>
                      handleSectionInstructionChange(
                        selectedSection,
                        e.target.value,
                      )
                    }
                    style={{
                      border: `2px solid ${sectionInstructions[selectedSection] ? '#6bd888' : '#dc3545'}`,
                      borderRadius: '8px',
                      padding: '10px',
                      height: '300px',
                    }}
                  />
                  {allSectionsReviewed && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: isSmallScreen ? '20px' : '-23px',
                        width: '100%',
                        paddingTop: '35px',
                      }}
                    >
                      <Button
                        color={allSectionsReviewed ? 'primary' : 'secondary'}
                        onClick={handleSave}
                        disabled={!allSectionsReviewed}
                        style={{
                          fontSize: '0.9rem',
                        }}
                      >
                        View Template
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <style>
            {`
    /* Styling for the scrollable container */
    .scrollable-container::-webkit-scrollbar {
      width: 6px; /* Adjust width for vertical scrollbar */
      height: 6px; /* Adjust height for horizontal scrollbar */
    }
    .scrollable-container::-webkit-scrollbar-thumb {
      background-color: #888;
      border-radius: 6px;
    }
    .scrollable-container::-webkit-scrollbar-thumb:hover {
      background-color: #555;
    }
    .scrollable-container::-webkit-scrollbar-track {
      background-color: #f0f0f0;
      border-radius: 6px;
    }

    /* Styling for textarea scrollbars */
    .scrollable-container textarea::-webkit-scrollbar {
      width: 6px; /* Adjust width for textarea vertical scrollbar */
      height: 6px; /* Adjust height for textarea horizontal scrollbar */
    }
    .scrollable-container textarea::-webkit-scrollbar-thumb {
      background-color: #888;
      border-radius: 6px;
    }
    .scrollable-container textarea::-webkit-scrollbar-thumb:hover {
      background-color: #555;
    }
    .scrollable-container textarea::-webkit-scrollbar-track {
      background-color: #f0f0f0;
      border-radius: 6px;
    }
  `}
          </style>
        </div>
      </div>
      {/* Animation Keyframes */}
      <style>
        {`
          @keyframes fadeInSlide {
            0% { opacity: 0; transform: translateY(-15px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Modal>
  );
};

export default PdfInstructionsModal;
